import React, { useState, useEffect } from "react";
import { Modal, Button, Space, Input, Checkbox, Radio, Tooltip, message } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  MinusCircleFilled,
  LoadingOutlined,
} from "@ant-design/icons";
import { configApi, tenantApi } from "../../../services/api";

function StatusIcon({ status }) {
  if (status === "created" || status === "updated")
    return <CheckCircleFilled className="text-emerald-600 text-[15px]" />;
  if (status === "skipped")
    return <MinusCircleFilled className="text-slate-400 text-[15px]" />;
  if (status === "error")
    return <CloseCircleFilled className="text-red-600 text-[15px]" />;
  if (status === "loading")
    return <LoadingOutlined className="text-indigo-500 text-[15px]" />;
  return null;
}

const STATUS_TEXT = { error: "text-red-600", skipped: "text-slate-400" };

export default function CopyToTenantsModal({ visible, onCancel, record, configType, currentEnv, tenantSchema }) {
  const isAlias = configType === "alias";
  const valueField = isAlias ? "alias_name" : "value";
  const sourceValue = record?.[valueField] || "";

  const [tenants, setTenants]               = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  // Per-tenant state: { selected, value }
  const [tenantStates, setTenantStates] = useState({});
  const [ifExists, setIfExists]         = useState("override"); // top-level: "override" | "skip"
  const [results, setResults]           = useState({});
  const [saving, setSaving]             = useState(false);
  const [done, setDone]                 = useState(false);

  useEffect(() => {
    if (!visible) return;
    setResults({});
    setDone(false);
    setTenantStates({});
    setTenants([]);
    setIfExists("override");
    setLoadingTenants(true);

    tenantApi.getCopyTargetsForEnv(currentEnv)
      .then((res) => {
        const others = (res.data || []).filter((t) => t.name !== tenantSchema);
        setTenants(others);
        const init = {};
        others.forEach((t) => {
          init[t.name] = { selected: true, value: sourceValue };
        });
        setTenantStates(init);
      })
      .catch((err) => {
        const data = err.response?.data || {};
        const detail = data.detail || data.message || data.error;
        message.error(detail ? `Failed to load tenants: ${detail}` : "Failed to load tenants", 10);
      })
      .finally(() => setLoadingTenants(false));
  }, [visible]); // eslint-disable-line

  const setTenantField = (schema, field, val) => {
    setTenantStates((prev) => ({ ...prev, [schema]: { ...prev[schema], [field]: val } }));
  };

  const handleCopy = async () => {
    const selected = tenants.filter((t) => tenantStates[t.name]?.selected);
    if (selected.length === 0) {
      message.warning("No tenants selected");
      return;
    }

    setSaving(true);
    const loadingMap = {};
    tenants.forEach((t) => {
      loadingMap[t.name] = { status: tenantStates[t.name]?.selected ? "loading" : "skipped" };
    });
    setResults(loadingMap);

    const targets = selected.map((t) => ({
      env: currentEnv,
      tenantSchema: t.name,
      action: ifExists, // top-level setting applies to all
      [valueField]: tenantStates[t.name]?.value ?? sourceValue,
    }));

    try {
      const res = await configApi.copyToEnvs({
        name: record.name,
        alias_name: record.alias_name,
        value: record.value,
        is_active: record.is_active,
        config_type: configType,
        sourceEnv: currentEnv,
        sourceTenant: tenantSchema,
        targets,
      });

      const resultMap = {};
      res.data.results.forEach((r) => { resultMap[r.tenantSchema] = { status: r.status, error: r.error || null }; });
      // Mark unchecked ones
      tenants.filter((t) => !tenantStates[t.name]?.selected)
             .forEach((t) => { resultMap[t.name] = { status: "skipped" }; });
      setResults(resultMap);
      setDone(true);
      const errors = res.data.results.filter((r) => r.status === "error");
      errors.length === 0
        ? message.success("Copied successfully")
        : message.warning(`Completed with ${errors.length} error(s)`);
    } catch (err) {
      message.error(err.response?.data?.error || "Copy failed");
      setResults({});
    } finally {
      setSaving(false);
    }
  };

  if (!record) return null;

  const ENV_SHORT = { dev: "DEV", stage: "STG", prod: "PROD" };
  const envClass = ENV_SHORT[currentEnv] ? currentEnv : "other";
  const envShort = ENV_SHORT[currentEnv] || currentEnv?.toUpperCase();

  return (
    <Modal
      title={
        <div className="flex items-center gap-2.5">
          <span className="text-[15px] font-semibold">Copy to Other Tenants</span>
          <span className="modal-name-chip">{record.name}</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={560}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onCancel}>{done ? "Close" : "Cancel"}</Button>
          {!done && (
            <Button type="primary" onClick={handleCopy} loading={saving} disabled={loadingTenants || tenants.length === 0}>
              Copy
            </Button>
          )}
        </Space>
      }
    >
      {/* Source */}
      <div className="section-box mb-4">
        <div className="mini-label mb-2">Source</div>
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className={`env-pill ${envClass}`}>{envShort}</span>
          <span className="text-[11px] text-slate-500">Tenant:</span>
          <span className="text-[12px] font-medium">{tenantSchema}</span>
          <span className="value-chip max-w-[200px]">
            {sourceValue || <span className="text-slate-400">empty</span>}
          </span>
        </div>
      </div>

      {/* Top-level: if already exists */}
      {!done && (
        <div className="section-box mb-4">
          <div className="mini-label mb-2">
            If config already exists in target tenant
          </div>
          <Radio.Group value={ifExists} onChange={(e) => setIfExists(e.target.value)} size="small">
            <Radio value="override">
              <span className="text-[12px] font-medium">Override</span>
              <span className="text-[11px] text-slate-400 ml-1">— replace existing value</span>
            </Radio>
            <Radio value="skip">
              <span className="text-[12px] font-medium">Skip</span>
              <span className="text-[11px] text-slate-400 ml-1">— keep existing, don't change</span>
            </Radio>
          </Radio.Group>
        </div>
      )}

      {/* Tenant list */}
      {loadingTenants ? (
        <div className="text-center py-6 text-slate-400">Loading tenants...</div>
      ) : tenants.length === 0 ? (
        <div className="text-center py-6 text-slate-400">No other tenants you can copy to in this environment.</div>
      ) : (
        <>
          {/* Select all / deselect all */}
          {!done && (
            <div className="flex items-center justify-between mb-2.5">
              <Checkbox
                checked={tenants.every((t) => tenantStates[t.name]?.selected)}
                indeterminate={
                  tenants.some((t) => !tenantStates[t.name]?.selected) &&
                  tenants.some((t) => tenantStates[t.name]?.selected)
                }
                onChange={(e) => {
                  setTenantStates((prev) => {
                    const next = { ...prev };
                    tenants.forEach((t) => { next[t.name] = { ...next[t.name], selected: e.target.checked }; });
                    return next;
                  });
                }}
              >
                <span className="text-[12px] font-semibold text-slate-600">Select All</span>
              </Checkbox>
              <span className="text-[11px] text-slate-400">
                {tenants.filter((t) => tenantStates[t.name]?.selected).length} of {tenants.length} selected
              </span>
            </div>
          )}

          <div className="flex flex-col gap-2 max-h-[360px] overflow-y-auto">
            {tenants.map((tenant) => {
              const state     = tenantStates[tenant.name] || {};
              const resultObj = results[tenant.name];
              const status    = resultObj?.status;
              const unchecked = !state.selected;

              return (
                <div key={tenant.name} className={`copy-target-card px-4 py-3 shrink-0${unchecked ? " is-unchecked" : ""}${unchecked && !status ? " is-dimmed" : ""}`}>
                  {/* Top row */}
                  <div className={`flex items-center justify-between ${unchecked || status ? "" : "mb-2.5"}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      {!status && (
                        <Checkbox
                          checked={state.selected}
                          onChange={(e) => setTenantField(tenant.name, "selected", e.target.checked)}
                          className="shrink-0"
                        />
                      )}
                      <span className="text-[13px] font-semibold text-slate-800 truncate">{tenant.subdomain || tenant.name}</span>
                      {tenant.subdomain && tenant.name !== tenant.subdomain && (
                        <span className="text-[11px] text-slate-400 shrink-0">{tenant.name}</span>
                      )}
                    </div>

                    {status && (
                      <Tooltip title={status === "error" ? (resultObj.error || "Unknown error") : null} placement="left">
                        <div className={`flex items-center gap-1.5 shrink-0 ${status === "error" ? "cursor-help" : "cursor-default"}`}>
                          <StatusIcon status={status} />
                          <span className={`text-[12px] font-medium capitalize ${STATUS_TEXT[status] || "text-emerald-600"}`}>
                            {status}
                          </span>
                        </div>
                      </Tooltip>
                    )}
                  </div>

                  {/* Value editor — shown only when selected */}
                  {state.selected && !status && (
                    <Input.TextArea
                      value={state.value}
                      onChange={(e) => setTenantField(tenant.name, "value", e.target.value)}
                      autoSize={{ minRows: 1, maxRows: 3 }}
                      placeholder="Value for this tenant..."
                      className="!text-[13px] w-full box-border"
                      size="small"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );
}
